import firebase from 'firebase/compat/app';
import { firestore, functions, storage } from '../../firebase';
import {
  EMPTY_TIPTAP_DOC,
  normalizeContentBankAccess,
  normalizeContentBankMetadata,
  toCallableJsonValue,
} from './contentBankUtils';
import type {
  ContentBankAccess,
  ContentBankDocument,
  ContentBankMedia,
  ContentBankMetadata,
  ContentBankSearchResponse,
  TiptapJsonNode,
} from './types';

export type SaveContentBankDraftInput = {
  documentId?: string;
  title: string;
  editorJson: TiptapJsonNode;
  metadata: ContentBankMetadata;
  access: ContentBankAccess;
  media?: ContentBankMedia[];
};

type SaveContentBankDraftResponse = {
  documentId: string;
};

type ListPublishedContentBankResponse = {
  documents: ContentBankDocument[];
};

type ContentBankAction =
  | 'saveDraft'
  | 'analyzeDraft'
  | 'publishDocument'
  | 'unpublishDocument'
  | 'listPublished'
  | 'search';

const contentBankCallable = functions.httpsCallable('contentBank');

async function callContentBank<TResponse>(action: ContentBankAction, payload: unknown = {}) {
  const response = await contentBankCallable(toCallableJsonValue({ action, payload }));
  return response.data as TResponse;
}

function mapContentBankDocumentData(id: string, data: firebase.firestore.DocumentData): ContentBankDocument {
  return {
    id,
    title: typeof data.title === 'string' ? data.title : '',
    slug: typeof data.slug === 'string' ? data.slug : id,
    status:
      data.status === 'published' || data.status === 'archived' || data.status === 'draft'
        ? data.status
        : 'draft',
    draft: data.draft
      ? {
          ...data.draft,
          metadata: normalizeContentBankMetadata(data.draft.metadata),
          access: normalizeContentBankAccess(data.draft.access),
          editorJson: data.draft.editorJson || EMPTY_TIPTAP_DOC,
          plainText: typeof data.draft.plainText === 'string' ? data.draft.plainText : '',
          title: typeof data.draft.title === 'string' ? data.draft.title : '',
        }
      : undefined,
    published: data.published
      ? {
          ...data.published,
          metadata: normalizeContentBankMetadata(data.published.metadata),
          access: normalizeContentBankAccess(data.published.access),
          editorJson: data.published.editorJson || EMPTY_TIPTAP_DOC,
          plainText: typeof data.published.plainText === 'string' ? data.published.plainText : '',
          title: typeof data.published.title === 'string' ? data.published.title : '',
          slug: typeof data.published.slug === 'string' ? data.published.slug : id,
          status: 'published',
          version: typeof data.published.version === 'number' ? data.published.version : 1,
        }
      : null,
    createdAt: data.createdAt,
    createdBy: data.createdBy,
    updatedAt: data.updatedAt,
    updatedBy: data.updatedBy,
  };
}

function mapDocumentSnapshot(
  snapshot: firebase.firestore.DocumentSnapshot<firebase.firestore.DocumentData>
): ContentBankDocument | null {
  if (!snapshot.exists) {
    return null;
  }

  return mapContentBankDocumentData(snapshot.id, snapshot.data() || {});
}

function sanitizeStorageFileName(fileName: string) {
  return fileName
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9._-]/g, '')
    .slice(0, 120);
}

export function subscribeToContentBankDocuments(
  onChange: (documents: ContentBankDocument[]) => void,
  onError: (error: Error) => void
) {
  return firestore
    .collection('contentBankDocuments')
    .onSnapshot((snapshot) => {
      const documents = snapshot.docs
        .map(mapDocumentSnapshot)
        .filter((document): document is ContentBankDocument => Boolean(document))
        .sort((first, second) => first.title.localeCompare(second.title));

      onChange(documents);
    }, onError);
}

export function subscribeToContentBankDocument(
  documentId: string,
  onChange: (document: ContentBankDocument | null) => void,
  onError: (error: Error) => void
) {
  return firestore
    .collection('contentBankDocuments')
    .doc(documentId)
    .onSnapshot((snapshot) => onChange(mapDocumentSnapshot(snapshot)), onError);
}

export function subscribeToPublishedContentBankDocuments(
  onChange: (documents: ContentBankDocument[]) => void,
  onError: (error: Error) => void
) {
  let isActive = true;

  callContentBank<ListPublishedContentBankResponse>('listPublished')
    .then((response) => {
      if (!isActive) {
        return;
      }

      const documents = (Array.isArray(response.documents) ? response.documents : [])
        .map((document) => mapContentBankDocumentData(document.id, document))
        .sort((first, second) => {
          const firstTitle = first.published?.title || first.title;
          const secondTitle = second.published?.title || second.title;
          return firstTitle.localeCompare(secondTitle);
        });

      onChange(documents);
    })
    .catch((error) => {
      if (isActive) {
        onError(error);
      }
    });

  return () => {
    isActive = false;
  };
}

export async function saveContentBankDraft(input: SaveContentBankDraftInput) {
  return callContentBank<SaveContentBankDraftResponse>('saveDraft', input);
}

export async function analyzeContentBankDraft(documentId: string) {
  return callContentBank<{ metadata: ContentBankMetadata }>('analyzeDraft', { documentId });
}

export async function publishContentBankDocument(
  documentId: string,
  metadata: ContentBankMetadata,
  access: ContentBankAccess
) {
  return callContentBank<{ documentId: string; embeddingStatus: string }>('publishDocument', {
    documentId,
    metadata,
    access,
  });
}

export async function unpublishContentBankDocument(documentId: string) {
  return callContentBank<{ documentId: string }>('unpublishDocument', { documentId });
}

export async function searchContentBank(query: string, limit = 8) {
  return callContentBank<ContentBankSearchResponse>('search', { query, limit });
}

export async function uploadContentBankImage(input: { documentId: string; file: File }) {
  const mediaId = firestore.collection('_').doc().id;
  const safeName = sanitizeStorageFileName(input.file.name) || `${mediaId}.jpg`;
  const storagePath = `content-bank/${input.documentId}/images/${mediaId}/${safeName}`;
  const snapshot = await storage.ref(storagePath).put(input.file, {
    contentType: input.file.type || 'image/jpeg',
    customMetadata: {
      originalFileName: input.file.name,
    },
  });
  const downloadUrl = await snapshot.ref.getDownloadURL();

  return {
    id: mediaId,
    type: 'image' as const,
    url: downloadUrl,
    storagePath,
    fileName: input.file.name,
    contentType: input.file.type || snapshot.metadata.contentType || 'image/jpeg',
  };
}
